import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useMatterStore } from './matterStore';
import { useSessionStore } from './sessionStore';
import { captureEvent } from '../analytics';

/**
 * A chat message in a dialog session. Subset of backend Chat model —
 * extend as consumers read additional fields.
 */
export interface Chat {
    RecordId: string;
    Trigger: 'USER' | 'AGENT';
    DisplayText: string;
    Timestamp: string;
    Raw: unknown;
    has_results?: boolean;
    has_debug_trace?: boolean;
    has_query_response?: boolean;
    HasDataframe?: boolean;
    AgenticWorkflowId?: string;
    IsComplete?: boolean;
}

interface SessionMetadata {
    CreatedTimestamp?: string;
    LastInteractionTimestamp?: string;
}

interface WorkflowProgress {
    workflow_id: string;
    status: string;
    completed_steps: string[];
    current_step_kind?: string | null;
    total_questions?: number | null;
    current_question_index?: number | null;
    subroutines_completed?: number | null;
    subroutines_total?: number | null;
}

/**
 * Subset of `DialogSession` used for syncing timestamps back into sessionStore.
 */
interface SessionLike {
    RecordId: string;
    LastInteractionTimestamp?: string;
    CreatedTimestamp?: string;
}

/**
 * Response shape shared by `/AskAgent` and `/ContinueWorkflow/{wfid}`.
 */
interface AskAgentResult {
    status: string;
    user_chat?: Chat;
    agent_chat?: Chat;
    session?: SessionLike;
    workflow_id?: string;
    completed_steps?: string[];
    current_step_kind?: string | null;
    total_questions?: number | null;
    current_question_index?: number | null;
    subroutines_completed?: number | null;
    subroutines_total?: number | null;
    clarification_question?: string;
    clarification_subroutine_id?: string;
}

/**
 * Parked-clarification state. `_resolve` / `_reject` are present when a live
 * `sendMessage()` promise is awaiting the user's answer; absent when the
 * clarification was restored from the backend on session load.
 */
interface PendingClarification {
    question: string;
    subroutine_id: string;
    workflow_id: string;
    matterId: string;
    sessionId: string;
    _resolve?: (result: AskAgentResult) => void;
    _reject?: (err: Error) => void;
}

/**
 * Payload passed to `answerClarification`. Free-text only.
 */
export interface ClarificationAnswerPayload {
    answer?: string;
}

/**
 * Handle to the optimistic chat bubbles created by `beginOptimisticSend`,
 * used by `sendMessage` to swap in the real chats (or roll back on error).
 */
export interface OptimisticSend {
    tempUserId: string;
    tempAgentId: string;
}

export const useChatStore = defineStore('chat', () => {
    const chats = ref<Chat[]>([]);
    const sessionMetadata = ref<SessionMetadata | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const currentDETDebugTrace = ref<Record<string, unknown> | null>(null);
    const showDETDebugPanel = ref(false);
    const detDebugLoading = ref(false);
    const matterContextStatus = ref<Record<string, unknown> | null>(null);
    const matterContextDebug = ref<Record<string, unknown> | null>(null);
    const showMatterContextDebug = ref(false);
    const matterContextDebugLoading = ref(false);
    const sending = ref(false);
    const workflowStatus = ref<WorkflowProgress | null>(null);
    const pendingClarification = ref<PendingClarification | null>(null);

    // Session-local record of which agent responses the user has rated this
    // session, keyed by chat RecordId -> 'positive' | 'negative'. Drives the
    // highlighted thumb and the one-submission-per-answer guard in the UI.
    // Not persisted across reloads — showing prior feedback state on load
    // needs a read endpoint that doesn't exist yet (deferred task).
    const dialogFeedback = ref<Record<string, 'positive' | 'negative'>>({});
    // RecordIds with an in-flight feedback POST, mapped to the rating being
    // submitted ('positive' | 'negative'). Disables the controls while the
    // request is outstanding and lets the UI show a spinner on the specific
    // thumb being submitted.
    const feedbackSubmitting = ref<Record<string, 'positive' | 'negative'>>({});

    const hasChats = computed(() => chats.value.length > 0);
    const chatCount = computed(() => chats.value.length);

    // True only once we have a definitive status saying the matter has no
    // published context yet. While the status is unknown (null — not yet
    // fetched, or a transient failure) this stays false so the matter is
    // never locked on missing/failed data. Drives the onboarding gate and
    // the matter-locked overlay; flips back to false reactively once
    // publish() refreshes the status to is_published: true.
    const matterContextRequired = computed(() => {
        const s = matterContextStatus.value;
        return !!s && s.is_published !== true;
    });

    // True only once a status has actually been fetched. Distinguishes "not
    // loaded yet / transient failure" (null) from a real payload, so callers
    // can avoid acting on the unknown state — e.g. the modal only shows its
    // "Loading saved matter context…" overlay once we know context exists.
    const matterContextPublished = computed(() => {
        const s = matterContextStatus.value;
        return !!s && s.is_published === true;
    });

    async function loadChats() {
        const matterStore = useMatterStore();
        const sessionStore = useSessionStore();

        if (!matterStore.currentMatter) {
            throw new Error('No matter selected');
        }
        if (!sessionStore.currentSession) {
            throw new Error('No session selected');
        }

        try {
            loading.value = true;
            error.value = null;
            const response = await fetch(
                `/api/Matter/${matterStore.currentMatter.RecordId}/DialogSession/${sessionStore.currentSession.RecordId}`,
            );
            if (!response.ok) {
                throw new Error('Failed to load chats');
            }
            const sessionData = await response.json();
            sessionMetadata.value = {
                LastInteractionTimestamp: sessionData.LastInteractionTimestamp,
                CreatedTimestamp: sessionData.CreatedTimestamp,
            };

            chats.value = sessionData.chats.reverse();

            await checkPendingClarification();

            // Check matter context status for this matter
            await fetchMatterContextStatus();
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Seed the in-flight workflow status plus optimistic user/agent chat
     * bubbles so the progress indicator renders immediately. `sendMessage`
     * calls this itself; callers that must await other work first (e.g.
     * creating the session for a brand-new dialog) can call it up front and
     * pass the returned seed to `sendMessage` so the indicator doesn't wait
     * on that round-trip.
     */
    function beginOptimisticSend(message: string): OptimisticSend {
        sending.value = true;
        workflowStatus.value = {
            workflow_id: '',
            status: 'in_progress',
            completed_steps: [],
            current_step_kind: null,
        };

        const tempUserId = `temp-user-${Date.now()}`;
        const tempAgentId = `temp-agent-${Date.now()}`;
        const optimisticUserChat: Chat = {
            RecordId: tempUserId,
            Trigger: 'USER',
            DisplayText: message,
            Timestamp: new Date().toISOString(),
            Raw: null,
            has_results: false,
            has_debug_trace: false,
        };
        // Optimistic agent placeholder — an in-progress bubble (IsComplete
        // false) so the progress indicator shows right away. Replaced by the
        // real agent chat once AskAgent returns, or removed on error.
        const optimisticAgentChat: Chat = {
            RecordId: tempAgentId,
            Trigger: 'AGENT',
            DisplayText: '',
            Timestamp: new Date().toISOString(),
            Raw: null,
            IsComplete: false,
        };
        chats.value.push(optimisticUserChat, optimisticAgentChat);
        return { tempUserId, tempAgentId };
    }

    /**
     * Roll back a `beginOptimisticSend` whose send never started (e.g.
     * session creation failed before `sendMessage` could run).
     */
    function cancelOptimisticSend(seed: OptimisticSend) {
        chats.value = chats.value.filter(
            (c) => c.RecordId !== seed.tempUserId && c.RecordId !== seed.tempAgentId,
        );
        workflowStatus.value = null;
        sending.value = false;
    }

    async function sendMessage(message: string, seed?: OptimisticSend) {
        const matterStore = useMatterStore();
        const sessionStore = useSessionStore();

        if (!matterStore.currentMatter) {
            throw new Error('No matter selected');
        }
        if (!sessionStore.currentSession) {
            throw new Error('No session selected');
        }

        // Onboarding-funnel signal: is this the first real question in the
        // session? Computed before the optimistic bubbles land, excluding the
        // `temp-` placeholders so a seeded send still reads correctly.
        const isFirstQuestion = !chats.value.some(
            (c) => c.Trigger === 'USER' && !c.RecordId.startsWith('temp-'),
        );

        const { tempUserId, tempAgentId } = seed ?? beginOptimisticSend(message);

        const matterId = matterStore.currentMatter.RecordId;
        const sessionId = sessionStore.currentSession.RecordId;

        try {
            const response = await fetch(
                `/api/Matter/${matterId}/DialogSession/${sessionId}/AskAgent`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        DisplayText: message,
                    }),
                },
            );

            if (!response.ok) {
                throw new Error('Failed to send message');
            }

            const result: AskAgentResult = await response.json();

            // Length (a count) is fine; never send the message text itself
            // (see analytics PII policy).
            captureEvent('question_asked', {
                matter_id: matterId,
                session_id: sessionId,
                is_first_question: isFirstQuestion,
                message_length: message.length,
            });

            const tempIndex = chats.value.findIndex((c) => c.RecordId === tempUserId);
            if (tempIndex !== -1 && result.user_chat) {
                chats.value[tempIndex] = result.user_chat;
            }

            _updateSessionMetadata(result.session);

            if (result.agent_chat) {
                const agentIdx = chats.value.findIndex((c) => c.RecordId === tempAgentId);
                if (agentIdx !== -1) {
                    chats.value[agentIdx] = result.agent_chat;
                } else {
                    chats.value.push(result.agent_chat);
                }
            }

            if (result.status === 'completed') {
                workflowStatus.value = null;
                return result;
            }

            if (!result.workflow_id) {
                throw new Error('Workflow did not return a workflow_id');
            }
            const workflowId = result.workflow_id;

            if (result.status === 'needs_clarification') {
                return await _handleClarification(result, matterId, sessionId, workflowId);
            }

            workflowStatus.value = {
                workflow_id: workflowId,
                status: result.status,
                completed_steps: result.completed_steps || [],
                current_step_kind: result.current_step_kind ?? null,
                total_questions: result.total_questions ?? null,
                current_question_index: result.current_question_index ?? null,
                subroutines_completed: result.subroutines_completed ?? null,
                subroutines_total: result.subroutines_total ?? null,
            };

            return await _pollWorkflow(matterId, sessionId, workflowId);
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            workflowStatus.value = null;
            // Drop the optimistic agent placeholder so a failed send doesn't
            // leave an empty in-progress bubble behind.
            const agentIdx = chats.value.findIndex((c) => c.RecordId === tempAgentId);
            if (agentIdx !== -1) {
                chats.value.splice(agentIdx, 1);
            }
            throw err;
        } finally {
            sending.value = false;
            workflowStatus.value = null;
        }
    }

    async function _pollWorkflow(
        matterId: string,
        sessionId: string,
        workflowId: string,
    ): Promise<AskAgentResult> {
        const POLL_DELAY_MS = 1000;
        const MAX_POLLS = 120;

        for (let i = 0; i < MAX_POLLS; i++) {
            await new Promise((resolve) => setTimeout(resolve, POLL_DELAY_MS));

            const response = await fetch(
                `/api/Matter/${matterId}/DialogSession/${sessionId}/ContinueWorkflow/${workflowId}`,
                { method: 'POST' },
            );

            if (!response.ok) {
                throw new Error('Failed to continue workflow');
            }

            const result: AskAgentResult = await response.json();

            workflowStatus.value = {
                workflow_id: workflowId,
                status: result.status,
                completed_steps: result.completed_steps || [],
                current_step_kind: result.current_step_kind ?? null,
                total_questions: result.total_questions ?? null,
                current_question_index: result.current_question_index ?? null,
                subroutines_completed: result.subroutines_completed ?? null,
                subroutines_total: result.subroutines_total ?? null,
            };

            _updateSessionMetadata(result.session);

            if (result.status === 'completed') {
                const agentChat = result.agent_chat;
                if (agentChat) {
                    const existingIdx = chats.value.findIndex(
                        (c) => c.RecordId === agentChat.RecordId,
                    );
                    if (existingIdx !== -1) {
                        chats.value[existingIdx] = agentChat;
                    } else {
                        chats.value.push(agentChat);
                    }
                }
                return result;
            }

            if (result.status === 'needs_clarification') {
                return await _handleClarification(result, matterId, sessionId, workflowId);
            }
        }

        throw new Error('Workflow timed out after 2 minutes of polling');
    }

    async function _handleClarification(
        result: AskAgentResult,
        matterId: string,
        sessionId: string,
        workflowId: string,
    ): Promise<AskAgentResult> {
        pendingClarification.value = {
            question: result.clarification_question || '',
            subroutine_id: result.clarification_subroutine_id || '',
            workflow_id: workflowId,
            matterId,
            sessionId,
        };

        return new Promise<AskAgentResult>((resolve, reject) => {
            const pc = pendingClarification.value;
            if (pc) {
                pc._resolve = resolve;
                pc._reject = reject;
            }
        });
    }

    async function answerClarification(payload: ClarificationAnswerPayload | string) {
        const clarification = pendingClarification.value;
        if (!clarification) {
            throw new Error('No pending clarification to answer');
        }

        const body: ClarificationAnswerPayload =
            typeof payload === 'string' ? { answer: payload } : { ...payload };
        if (body.answer === undefined) {
            body.answer = '';
        }

        const { subroutine_id, workflow_id, matterId, sessionId, _resolve, _reject } =
            clarification;
        pendingClarification.value = null;

        const isRestored = !_resolve;
        if (isRestored) {
            sending.value = true;
            workflowStatus.value = {
                workflow_id,
                status: 'in_progress',
                completed_steps: [],
                current_step_kind: null,
            };
        }

        try {
            const answerResponse = await fetch(
                `/api/Matter/${matterId}/DialogSession/${sessionId}/AnswerClarification/${subroutine_id}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                },
            );

            if (!answerResponse.ok) {
                throw new Error('Failed to save clarification answer');
            }

            // The user answered an agent disambiguation prompt. Emitted on
            // the answer commit, independent of how the resumed workflow
            // resolves. ids only — never the answer text.
            captureEvent('clarification_answered', {
                matter_id: matterId,
                session_id: sessionId,
            });

            const result = await _pollWorkflow(matterId, sessionId, workflow_id);

            if (_resolve) {
                _resolve(result);
            }
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            if (_reject) {
                _reject(err instanceof Error ? err : new Error(String(err)));
            }
        } finally {
            if (isRestored) {
                sending.value = false;
                workflowStatus.value = null;
            }
        }
    }

    async function checkPendingClarification() {
        const matterStore = useMatterStore();
        const sessionStore = useSessionStore();

        if (!matterStore.currentMatter || !sessionStore.currentSession) {
            return;
        }

        const matterId = matterStore.currentMatter.RecordId;
        const sessionId = sessionStore.currentSession.RecordId;

        try {
            const response = await fetch(
                `/api/Matter/${matterId}/DialogSession/${sessionId}/PendingClarification`,
            );
            if (!response.ok) return;

            const result = await response.json();
            if (result.pending) {
                pendingClarification.value = {
                    question: result.question,
                    subroutine_id: result.subroutine_id,
                    workflow_id: result.workflow_id,
                    matterId,
                    sessionId,
                };
            }
        } catch (err) {
            console.warn('Failed to check pending clarification:', err);
        }
    }

    function _updateSessionMetadata(session: SessionLike | null | undefined) {
        if (!session) return;
        sessionMetadata.value = {
            LastInteractionTimestamp: session.LastInteractionTimestamp,
            CreatedTimestamp: session.CreatedTimestamp,
        };

        const sessionStore = useSessionStore();
        if (
            sessionStore.currentSession &&
            sessionStore.currentSession.RecordId === session.RecordId
        ) {
            sessionStore.currentSession.LastInteractionTimestamp = session.LastInteractionTimestamp;
        }

        const sessionIndex = sessionStore.sessions.findIndex(
            (s) => s.RecordId === session.RecordId,
        );
        if (sessionIndex !== -1) {
            sessionStore.sessions[sessionIndex].LastInteractionTimestamp =
                session.LastInteractionTimestamp;
        }
    }

    function clearChats() {
        chats.value = [];
        sessionMetadata.value = null;
        // Drop any error from the previous session — otherwise a failed
        // sendMessage / loadChats keeps surfacing in ChatWindow after the
        // user has switched session or matter.
        error.value = null;
    }

    // --- DET debug trace auto-refresh ------------------------------------
    // While the open trace's workflow is still running (no
    // completed_timestamp yet), silently refetch on an interval so the panel
    // fills in as steps complete. Stops once the workflow completes, the
    // panel closes, or a different workflow's trace is requested.
    const DET_DEBUG_REFRESH_MS = 2000;
    let detDebugRefreshTimer: ReturnType<typeof setTimeout> | null = null;
    let detDebugWorkflowId: string | null = null;

    function _clearDETDebugRefresh() {
        if (detDebugRefreshTimer !== null) {
            clearTimeout(detDebugRefreshTimer);
            detDebugRefreshTimer = null;
        }
    }

    function _scheduleDETDebugRefresh(workflowId: string) {
        const trace = currentDETDebugTrace.value;
        if (!showDETDebugPanel.value || detDebugWorkflowId !== workflowId) return;
        if (!trace || trace.completed_timestamp) return;

        _clearDETDebugRefresh();
        detDebugRefreshTimer = setTimeout(async () => {
            detDebugRefreshTimer = null;
            const matterStore = useMatterStore();
            if (
                !showDETDebugPanel.value ||
                detDebugWorkflowId !== workflowId ||
                !matterStore.currentMatter
            ) {
                return;
            }
            try {
                // No loading flag and no trace reset — swap the data in
                // place so the open panel doesn't flash back to a spinner.
                const response = await fetch(
                    `/api/Matter/${matterStore.currentMatter.RecordId}/DETDebug/${workflowId}`,
                );
                if (
                    response.ok &&
                    showDETDebugPanel.value &&
                    detDebugWorkflowId === workflowId
                ) {
                    currentDETDebugTrace.value = await response.json();
                }
            } catch (err) {
                console.warn('DET debug trace auto-refresh failed:', err);
            }
            _scheduleDETDebugRefresh(workflowId);
        }, DET_DEBUG_REFRESH_MS);
    }

    async function fetchDETDebugTrace(workflowId: string) {
        const matterStore = useMatterStore();
        if (!matterStore.currentMatter) {
            throw new Error('No matter selected');
        }
        _clearDETDebugRefresh();
        detDebugWorkflowId = workflowId;
        // Open the panel immediately so the user gets feedback; the body
        // shows a loading indicator until the fetch resolves.
        currentDETDebugTrace.value = null;
        showDETDebugPanel.value = true;
        detDebugLoading.value = true;
        try {
            const response = await fetch(
                `/api/Matter/${matterStore.currentMatter.RecordId}/DETDebug/${workflowId}`,
            );
            if (!response.ok) {
                if (response.status === 404) {
                    console.warn('No DET debug trace available for this workflow');
                    return null;
                }
                throw new Error('Failed to fetch DET debug trace');
            }
            currentDETDebugTrace.value = await response.json();
            _scheduleDETDebugRefresh(workflowId);
            return currentDETDebugTrace.value;
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        } finally {
            detDebugLoading.value = false;
        }
    }

    function closeDETDebugPanel() {
        _clearDETDebugRefresh();
        detDebugWorkflowId = null;
        showDETDebugPanel.value = false;
        currentDETDebugTrace.value = null;
        detDebugLoading.value = false;
    }

    // Returns the parsed status on a successful response, or null when the
    // request couldn't be made or failed. Callers gating onboarding rely on
    // this distinction: a null result is a transient failure (fail open,
    // don't prompt), whereas a real {is_published: false} payload means the
    // matter genuinely has no published context yet.
    async function fetchMatterContextStatus(
        matterId?: string,
    ): Promise<Record<string, unknown> | null> {
        const matterStore = useMatterStore();
        const targetId = matterId ?? matterStore.currentMatter?.RecordId;
        if (!targetId) return null;
        try {
            const response = await fetch(`/api/Matter/${targetId}/MatterContext/Status`);
            if (response.ok) {
                const status = await response.json();
                matterContextStatus.value = status;
                return status;
            }
        } catch (err) {
            console.warn('Failed to fetch matter context status:', err);
        }
        return null;
    }

    // Optimistically mark the current matter's context as unpublished without
    // a server round-trip. A just-created matter is definitionally unpublished,
    // so seeding this lets the "setup incomplete" banner / onboarding gate
    // render on the first frame instead of waiting on the modal's
    // /MatterContext/Status fetch. That fetch still runs afterward and
    // reconciles with the server (it confirms is_published: false).
    function markMatterContextUnpublished() {
        matterContextStatus.value = { is_published: false };
    }

    async function fetchMatterContextDebug(matterId?: string) {
        const matterStore = useMatterStore();
        const targetId = matterId ?? matterStore.currentMatter?.RecordId;
        if (!targetId) return;
        // Open the overlay immediately so the user gets feedback; the body
        // shows a loading indicator until the fetch resolves.
        matterContextDebug.value = null;
        showMatterContextDebug.value = true;
        matterContextDebugLoading.value = true;
        try {
            const response = await fetch(`/api/Matter/${targetId}/MatterContext/Debug`);
            if (response.ok) {
                matterContextDebug.value = await response.json();
            }
        } catch (err) {
            console.error('Failed to fetch matter context debug:', err);
        } finally {
            matterContextDebugLoading.value = false;
        }
    }

    function closeMatterContextDebug() {
        showMatterContextDebug.value = false;
        matterContextDebug.value = null;
        matterContextDebugLoading.value = false;
    }

    // Hard-delete all matter context data for a matter. On success, clears any
    // cached status/debug state and re-fetches status so banners and gating
    // reflect the now-empty matter. Returns true on success, false otherwise.
    async function deleteMatterContext(matterId?: string): Promise<boolean> {
        const matterStore = useMatterStore();
        const targetId = matterId ?? matterStore.currentMatter?.RecordId;
        if (!targetId) return false;
        try {
            const response = await fetch(`/api/Matter/${targetId}/MatterContext`, {
                method: 'DELETE',
            });
            if (!response.ok) return false;
            // Destructive — reverses the matter-context onboarding step.
            captureEvent('matter_context_deleted', { matter_id: targetId });
            closeMatterContextDebug();
            matterContextStatus.value = null;
            await fetchMatterContextStatus(targetId);
            return true;
        } catch (err) {
            console.error('Failed to delete matter context:', err);
            return false;
        }
    }

    // Record the user's thumbs-up/down on a single agent response via POST
    // /Feedback (the shared feedback endpoint, which accepts a
    // dialog_response_id for the chat surface). The chat's RecordId is the
    // dialog_response_id. `issueReported` / `feedbackDetails` are the optional
    // negative-feedback dialog fields. On success the rating is recorded in
    // dialogFeedback so the UI can fill the chosen thumb to reflect the last
    // selection; feedback may be re-submitted at any time, overwriting it.
    // Always resolves; returns true on success, false on failure (the caller
    // surfaces the error).
    //
    // `silent` is for the negative-feedback dialog's Cancel path: the negative
    // flag is still posted to the backend, but no in-flight or recorded UI
    // state is touched (the thumb is not highlighted/locked), so the user sees
    // nothing.
    async function submitDialogFeedback(
        responseId: string,
        payload: {
            isPositive: boolean;
            issueReported?: string;
            feedbackDetails?: string;
            silent?: boolean;
        },
    ): Promise<boolean> {
        const matterStore = useMatterStore();
        const matterId = matterStore.currentMatter?.RecordId;
        if (!matterId || !responseId) return false;

        if (!payload.silent) {
            feedbackSubmitting.value = {
                ...feedbackSubmitting.value,
                [responseId]: payload.isPositive ? 'positive' : 'negative',
            };
        }
        try {
            const response = await fetch(`/api/Matter/${matterId}/Feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    is_positive: payload.isPositive,
                    issue_reported: payload.issueReported || null,
                    feedback_details: payload.feedbackDetails || null,
                    dialog_response_id: responseId,
                }),
            });
            if (!response.ok) return false;
            if (!payload.silent) {
                dialogFeedback.value = {
                    ...dialogFeedback.value,
                    [responseId]: payload.isPositive ? 'positive' : 'negative',
                };
            }
            // `has_details` is a boolean, not the free-text the user typed
            // (which can contain anything — never send it).
            captureEvent('response_feedback_given', {
                matter_id: matterId,
                rating: payload.isPositive ? 'positive' : 'negative',
                silent: payload.silent === true,
                has_details: Boolean(payload.feedbackDetails),
            });
            return true;
        } catch (err) {
            console.error('Failed to submit dialog feedback:', err);
            return false;
        } finally {
            if (!payload.silent) {
                const next = { ...feedbackSubmitting.value };
                delete next[responseId];
                feedbackSubmitting.value = next;
            }
        }
    }

    return {
        chats,
        sessionMetadata,
        loading,
        error,
        dialogFeedback,
        feedbackSubmitting,
        submitDialogFeedback,
        currentDETDebugTrace,
        showDETDebugPanel,
        detDebugLoading,
        matterContextStatus,
        matterContextRequired,
        matterContextPublished,
        matterContextDebug,
        showMatterContextDebug,
        matterContextDebugLoading,
        sending,
        workflowStatus,
        pendingClarification,
        hasChats,
        chatCount,
        loadChats,
        beginOptimisticSend,
        cancelOptimisticSend,
        sendMessage,
        answerClarification,
        checkPendingClarification,
        clearChats,
        fetchDETDebugTrace,
        closeDETDebugPanel,
        fetchMatterContextStatus,
        markMatterContextUnpublished,
        fetchMatterContextDebug,
        closeMatterContextDebug,
        deleteMatterContext,
    };
});
